import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Spin, Select } from "antd";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

export default function PayrollSummary() {
    const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };

    // Default month & year
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const [organisations, setOrganisations] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [organisationId, setOrganisationId] = useState(1);
    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(currentYear);

    // Fetch organisations
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.error("Error fetching organisations:", err);
        }
    };

    useEffect(() => {
        fetchOrganisations();
    }, []);

    // Fetch payroll summary
    useEffect(() => {
        const fetchPayrollSummary = async () => {
            setLoading(true);
            try {
                const res = await axios.get(
                    `${backUrl}/api/reports/payroll-summary/?organisation_id=${organisationId}&month=${month}&year=${year}`,
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
                setData(res.data);
            } catch (error) {
                console.error("Error fetching payroll summary:", error);
            } finally {
                setLoading(false);
            }
        };

        if (organisationId) fetchPayrollSummary();
    }, [organisationId, month, year, backUrl]);

    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];

    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    const chartData = data
        ? [
            {
                name: "Payroll Summary",
                "Gross Salary": parseFloat(data.gross_salary) || 0,
                "Deductions": parseFloat(data.total_deductions) || 0,
                "Net Salary": parseFloat(data.net_salary) || 0,
            },
        ]
        : [];

    return (
        <div className="p-6 bg-gray-50 min-h-screen flex flex-col justify-start items-center">
            {/* Filters */}
            <div className="flex gap-4 mb-6">
                <Select
                    value={organisationId}
                    onChange={(value) => setOrganisationId(value)}
                    style={{ width: 280 }}
                >
                    {organisations.map((org) => (
                        <Option key={org.id} value={org.id}>
                            {org.name}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={month}
                    onChange={(value) => setMonth(value)}
                    style={{ width: 120, marginLeft: '2px' }}
                >
                    {months.map((m, index) => (
                        <Option key={index + 1} value={index + 1}>
                            {m}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={year}
                    onChange={(value) => setYear(value)}
                    style={{ width: 120, marginLeft: '2px' }}
                >
                    {years.map((y) => (
                        <Option key={y} value={y}>
                            {y}
                        </Option>
                    ))}
                </Select>
            </div>

            {/* Chart */}
            {loading ? (
                <Spin size="large" />
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <Card
                        title={`Payroll Summary (${months[month - 1]} ${year})`}
                        bordered={false}
                        className="shadow-md rounded-2xl w-[650px]"
                        style={{
                            marginTop: 20

                        }}
                    >
                        <ResponsiveContainer width="100%" height={450}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip formatter={(val) => `₹${val.toLocaleString()}`} />
                                <Legend />
                                <Bar dataKey="Gross Salary" fill="#3B82F6" />
                                <Bar dataKey="Deductions" fill="#EF4444" />
                                <Bar dataKey="Net Salary" fill="#22C55E" />
                            </BarChart>
                        </ResponsiveContainer>

                        <div className="text-center mt-4 text-gray-700">
                            <p>
                                <strong>Gross Salary:</strong> ₹{data?.gross_salary || 0} |{" "}
                                <strong>Deductions:</strong> ₹{data?.total_deductions || 0} |{" "}
                                <strong>Net Salary:</strong> ₹{data?.net_salary || 0}
                            </p>
                        </div>
                    </Card>
                </motion.div>
            )}
        </div>
    );
}
